import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import type { IHttpError, RootState } from "Types";

import {
  addProgressAction,
  isActionInProgressSelector,
  removeProgressAction,
} from "../utils/loader/progress.slice";
import { httpGet, httpPost } from "../services/api/axiosHelpers";
import { handleAPIError } from "../utils/error.util";
import { AccountStatusEnum } from "../account-status/account-status.types";
import type {
  AccountResponse,
  FetchAccountArgs,
  ShippingAccountState,
  ShippingRow,
} from "./types/ShippingAccount.types";
import { accountUrls, globalUrls } from "../services/api/api-urls";
import { initialState } from "./types/AccountState";
import type {
  DutiesAndTaxesState,
  GuaranteedDutyRow,
} from "./types/DutiesAndTaxes";
import type {
  CarrierRegistrationRow,
  CarrierRegistrationState,
  PutCarrierRegistrationState,
} from "./types/CarrierRegistration.types";
import type { FAQItem, FaqsState } from "./types/FAQCard.types";
import { getInfoCards } from "../account-status/utils";
import { getNameFromCode } from "../utils";
import {
  selectCountriesData,
  selectDestinationCountriesData,
  selectProductCategoriesData,
  selectSourceCountriesData,
  selectStatesData,
} from "../onboarding/onboarding.slice";

export const fetchShippingAccounts = createAsyncThunk<
  ShippingRow[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "account/fetchShippingAccounts",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchShippingAccounts" }));
      const response = await httpGet({
        useDummyToken,
        url: accountUrls.shippingAccounts,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "account.slice.ts - fetchShippingAccounts",
      });
    } finally {
      dispatch(removeProgressAction("fetchShippingAccounts"));
    }
  },
);

export const fetchDutiesAndTaxes = createAsyncThunk<
  GuaranteedDutyRow[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "account/fetchDutiesAndTaxes",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchDutiesAndTaxes" }));
      const response = await httpGet({
        useDummyToken,
        url: accountUrls.dutiesTaxes,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "account.slice.ts - fetchDutiesAndTaxes",
      });
    } finally {
      dispatch(removeProgressAction("fetchDutiesAndTaxes"));
    }
  },
);

export const fetchCarrierRegistrations = createAsyncThunk<
  CarrierRegistrationRow[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "account/fetchCarrierRegistrations",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchCarrierRegistrations" }));
      const response = await httpGet({
        useDummyToken,
        url: accountUrls.carrierRegistrations,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "account.slice.ts - fetchCarrierRegistrations",
      });
    } finally {
      dispatch(removeProgressAction("fetchCarrierRegistrations"));
    }
  },
);

export const putCarrierRegistration = createAsyncThunk<
  boolean,
  { useDummyToken?: boolean },
  {
    rejectValue: IHttpError;
  }
>(
  "account/putCarrierRegistration",
  async ({ useDummyToken }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "putCarrierRegistration" }));
      await httpPost({
        useDummyToken,
        url: accountUrls.carrierRegistrations,
        signal,
      });
      dispatch(removeProgressAction("putCarrierRegistration"));

      // refetch carrier registration data on successful put
      dispatch(fetchCarrierRegistrations({ useDummyToken, _signal: signal }));
      // refetch account data on successful put
      dispatch(fetchAccount({ useDummyToken, _signal: signal }));
      return true;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "account.slice.ts - putCarrierRegistration",
      });
    } finally {
      dispatch(removeProgressAction("putCarrierRegistration"));
    }
  },
);

export const fetchFaqs = createAsyncThunk<
  FAQItem[],
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "account/fetchFaqs",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchFaqs" }));
      const response = await httpGet({
        useDummyToken,
        url: globalUrls.faqs,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "account.slice.ts - fetchFaqs",
      });
    } finally {
      dispatch(removeProgressAction("fetchFaqs"));
    }
  },
);

const normalizeAccountStatus = (status?: string): AccountStatusEnum => {
  if (!status) {
    return AccountStatusEnum.NOT_SUBMITTED;
  }

  const trimmedStatus = status.trim();
  if (trimmedStatus === "Submitted") {
    return AccountStatusEnum.NOT_SUBMITTED;
  }

  return trimmedStatus as AccountStatusEnum;
};

const buildAccountStatusProgress = (
  status: AccountStatusEnum,
): Array<{
  status: AccountStatusEnum;
  label: string;
  isCompleted: boolean;
}> => {
  const statusOrder = [
    AccountStatusEnum.NOT_SUBMITTED,
    AccountStatusEnum.SUBMITTED,
    AccountStatusEnum.PENDING_ACTIVATION,
    AccountStatusEnum.PAYMENT_DETAILS_RECEIVED,
    AccountStatusEnum.ACTIVATED,
    AccountStatusEnum.REGISTERED_CARRIER_ACCOUNT,
  ];

  const labels: Record<AccountStatusEnum, string> = {
    [AccountStatusEnum.NOT_SUBMITTED]: "Download App & Accept T&Cs",
    [AccountStatusEnum.SUBMITTED]: "Information Submitted",
    [AccountStatusEnum.PENDING_ACTIVATION]: "Account Creation In Progress",
    [AccountStatusEnum.PAYMENT_DETAILS_RECEIVED]: "Payment Details Received",
    [AccountStatusEnum.ACTIVATED]: "Account Creation Complete",
    [AccountStatusEnum.REGISTERED_CARRIER_ACCOUNT]:
      "App Registered – Start Shipping!",
  };

  return statusOrder.map((statusValue) => ({
    status: statusValue,
    label: labels[statusValue],
    isCompleted:
      status === AccountStatusEnum.NOT_SUBMITTED
        ? statusValue === AccountStatusEnum.NOT_SUBMITTED
        : statusOrder.indexOf(statusValue) <= statusOrder.indexOf(status),
  }));
};

export const fetchAccount = createAsyncThunk<
  AccountResponse,
  FetchAccountArgs,
  {
    rejectValue: IHttpError;
  }
>(
  "account/fetchAccount",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchAccount" }));
      const response = await httpGet({
        useDummyToken,
        url: accountUrls.account,
        signal: _signal || signal,
      });
      const payload = response?.data as Partial<AccountResponse> | undefined;
      const normalizedStatus = normalizeAccountStatus(payload?.status);

      return {
        ...payload,
        companyCountry: payload?.companyCountry ?? payload?.country,
        status: normalizedStatus,
        statusProgress: buildAccountStatusProgress(normalizedStatus),
      } as AccountResponse;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "account.slice.ts - fetchAccount",
      });
    } finally {
      dispatch(removeProgressAction("fetchAccount"));
    }
  },
);

export const appSlice = createSlice({
  name: "account",
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearShippingAccountsState: (state) => {
      state.shippingAccountsState = initialState.shippingAccountsState;
    },
    clearDutiesAndTaxesState: (state) => {
      state.dutiesAndTaxesState = initialState.dutiesAndTaxesState;
    },
    clearCarrierRegistrationsState: (state) => {
      state.carrierRegistrationsState = initialState.carrierRegistrationsState;
    },
    clearPutCarrierRegistrationsState: (state) => {
      state.putCarrierRegistrationState =
        initialState.putCarrierRegistrationState;
    },
    clearFaqsState: (state) => {
      state.faqsState = initialState.faqsState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShippingAccounts.pending, (state) => {
        state.shippingAccountsState.status = "loading";
      })
      .addCase(fetchShippingAccounts.fulfilled, (state, action) => {
        state.shippingAccountsState.status = "idle";
        state.shippingAccountsState.data = action.payload;
      })
      .addCase(fetchShippingAccounts.rejected, (state, action) => {
        state.shippingAccountsState.status = "failed";
        state.shippingAccountsState.error = action.payload || action.error;
      })
      .addCase(fetchDutiesAndTaxes.pending, (state) => {
        state.dutiesAndTaxesState.status = "loading";
      })
      .addCase(fetchDutiesAndTaxes.fulfilled, (state, action) => {
        state.dutiesAndTaxesState.status = "idle";
        state.dutiesAndTaxesState.data = action.payload;
      })
      .addCase(fetchDutiesAndTaxes.rejected, (state, action) => {
        state.dutiesAndTaxesState.status = "failed";
        state.dutiesAndTaxesState.error = action.payload || action.error;
      })
      .addCase(fetchCarrierRegistrations.pending, (state) => {
        state.carrierRegistrationsState.status = "loading";
      })
      .addCase(fetchCarrierRegistrations.fulfilled, (state, action) => {
        state.carrierRegistrationsState.status = "idle";
        state.carrierRegistrationsState.data = action.payload;
      })
      .addCase(fetchCarrierRegistrations.rejected, (state, action) => {
        state.carrierRegistrationsState.status = "failed";
        state.carrierRegistrationsState.error = action.payload || action.error;
      })
      .addCase(putCarrierRegistration.pending, (state) => {
        state.putCarrierRegistrationState.status = "loading";
      })
      .addCase(putCarrierRegistration.fulfilled, (state, action) => {
        state.putCarrierRegistrationState.status = "idle";
        state.putCarrierRegistrationState.data = action.payload;
      })
      .addCase(putCarrierRegistration.rejected, (state, action) => {
        state.putCarrierRegistrationState.status = "failed";
        state.putCarrierRegistrationState.data = false;
        state.putCarrierRegistrationState.error =
          action.payload || action.error;
      })
      .addCase(fetchFaqs.pending, (state) => {
        state.faqsState.status = "loading";
      })
      .addCase(fetchFaqs.fulfilled, (state, action) => {
        state.faqsState.status = "idle";
        state.faqsState.data = action.payload;
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.faqsState.status = "failed";
        state.faqsState.error = action.payload || action.error;
      })

      .addCase(fetchAccount.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAccount.fulfilled, (state, action) => {
        state.status = "idle";
        state.preActivationAccountState.data = action.payload;
        state.error = null;
      })
      .addCase(fetchAccount.rejected, (state, action) => {
        state.status = "failed";
        state.preActivationAccountState.error = action.payload || action.error;
      });
  },
});

export const {
  clear,
  clearShippingAccountsState,
  clearDutiesAndTaxesState,
  clearCarrierRegistrationsState,
  clearPutCarrierRegistrationsState,
  clearFaqsState,
} = appSlice.actions;

export const selectShippingAccountsState = (
  state: RootState,
): ShippingAccountState => state.account.shippingAccountsState;
export const isFetchShippingAccountsInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchShippingAccounts");

export const selectDutiesAndTaxesState = (
  state: RootState,
): DutiesAndTaxesState => state.account.dutiesAndTaxesState;
export const isFetchDutiesAndTaxesInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchDutiesAndTaxes");

export const selectCarrierRegistrationsState = (
  state: RootState,
): CarrierRegistrationState => state.account.carrierRegistrationsState;

export const isFetchCarrierRegistrationsInProgressSelector = (
  state: RootState,
) => isActionInProgressSelector(state, "fetchCarrierRegistrations");

export const selectPutCarrierRegistrationState = (
  state: RootState,
): PutCarrierRegistrationState => state.account.putCarrierRegistrationState;
export const isPutCarrierRegistrationInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "putCarrierRegistration");

export const selectFaqsState = (state: RootState): FaqsState =>
  state.account.faqsState;

export const isFetchFaqsInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchFaqs");

export const selectPreActivationAccountState = (state: RootState) =>
  state.account.preActivationAccountState;

export const selectPreActivationAccountStatus = createSelector(
  [selectPreActivationAccountState],
  (preActivationAccountState) =>
    preActivationAccountState?.data?.status ?? null,
);

const EMPTY_STEPPER_STATE: Array<{ label: string; isCompleted: boolean }> = [];

export const selectPreActivationAccountStepperState = createSelector(
  [selectPreActivationAccountState],
  (preActivationAccountState) =>
    preActivationAccountState?.data?.statusProgress ?? EMPTY_STEPPER_STATE,
);

export const isFetchAccountInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchAccount");

export const selectAccountApiStatus = (state: RootState) =>
  state.account.status;

export const selectAccountStateDataCards = createSelector(
  [
    selectPreActivationAccountState,
    selectSourceCountriesData,
    selectCountriesData,
    selectDestinationCountriesData,
    selectProductCategoriesData,
    selectStatesData,
  ],
  (
    preActivationAccountState,
    sourceCountries,
    countries,
    destinationCountries,
    productCategories,
    statesData,
  ) => {
    const data: AccountResponse = preActivationAccountState.data ?? [];
    const companyCountryCode = data.companyCountry ?? "";
    // apply code to name transformations
    const sourceCountryName = getNameFromCode({
      code: data.country,
      sourceArray: sourceCountries,
    });
    const companyCountryName = getNameFromCode({
      code: companyCountryCode,
      sourceArray: countries,
    });
    const destinationCountryNames = data.destinationCountries?.length
      ? data.destinationCountries.map((code) =>
          getNameFromCode({
            code,
            sourceArray: destinationCountries,
          }),
        )
      : [];
    const productCategoryNames = data.productCategories?.length
      ? data.productCategories.map((code) =>
          getNameFromCode({
            code,
            sourceArray: productCategories,
          }),
        )
      : [];
    const stateName = getNameFromCode({
      code: data.state,
      sourceArray: statesData ?? [],
    });

    return getInfoCards({
      ...data,
      state: stateName,
      country: sourceCountryName,
      companyCountry: companyCountryName,
      destinationCountries: destinationCountryNames,
      productCategories: productCategoryNames,
    });
  },
);

export default appSlice.reducer;
